import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { EmailPipe, PasswordPipe, VerificationCodePipe } from '@zero/onboarding/domain';
import { FooterComponent } from '@zero/onboarding/feature-onboarding/web';
import { UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, InputComponent } from '@zero/shared/ui-web-design-system';
import { ValidationPatterns } from '@zero/shared/util-models';
import { AuthService, RouteOptionsService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

@Component({
  templateUrl: './forgot-password.component.html',
  imports: [
    AsyncPipe,
    FormsModule,
    TranslocoPipe,
    RouterLink,
    UserMessagesComponent,
    ButtonComponent,
    FooterComponent,
    InputComponent,
    EmailPipe,
    PasswordPipe,
    VerificationCodePipe,
  ],
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  readonly #email = new BehaviorSubject<string>('');
  readonly #password = new BehaviorSubject<string>('');
  readonly #verificationCode = new BehaviorSubject<string>('');
  readonly #authService = inject(AuthService);
  readonly #msgService = inject(UserMessagesHolderService);
  readonly #router = inject(Router);
  readonly #routeOptionsService = inject(RouteOptionsService);

  public readonly commonRoutes = commonRoutes;
  public validatedAtLeastOnce = false;
  public showPasswordResetRequiredMessage = false;
  public displayHelp = false;
  public readonly passwordPattern: RegExp = ValidationPatterns.passwordPattern;
  public readonly allowedSpecialCharacters = ValidationPatterns.allowedSpecialCharacters;

  public readonly email$ = this.#email.asObservable();
  public readonly password$ = this.#password.asObservable();
  public readonly verificationCode$ = this.#verificationCode.asObservable();

  public readonly isValidInitialForm$ = this.email$.pipe(map((email) => !!email && new EmailPipe().transform(email)));
  public readonly isValidSetPasswordForm$ = combineLatest([this.email$, this.verificationCode$, this.password$]).pipe(
    map(([email, verificationCode, password]) => {
      const emailIsValid = !!email && new EmailPipe().transform(email);
      const verificationCodeIsValid = !!verificationCode && new VerificationCodePipe().transform(verificationCode);
      const passwordIsValid = !!password && new PasswordPipe().transform(password);
      return emailIsValid && verificationCodeIsValid && passwordIsValid;
    }),
  );

  ngOnInit(): void {
    this.#routeOptionsService.toggleGradient();
    this.#routeOptionsService.hideHeaderAndFooter();
    this.showPasswordResetRequiredMessage = this.#authService.isPasswordResetRequired();
  }

  ngOnDestroy(): void {
    this.#routeOptionsService.toggleGradient();
    this.#routeOptionsService.displayHeaderAndFooter();
  }

  public handleEmailChange = (email: string) => this.#email.next(email.toLowerCase());
  public handlePasswordChange = (password: string) => this.#password.next(password);
  public handleVerificationCodeChange = (verificationCode: string) => this.#verificationCode.next(verificationCode);

  public onSubmit(): void {
    this.validatedAtLeastOnce = true;
    this.#msgService.clearAllMessages();
    this.#authService.forgotPassword(this.#email.value).subscribe((result) => {
      if (result) {
        this.#msgService.addSuccessMessage(
          'Falls die von Dir angegebene E-Mail-Adresse bei uns existiert, haben wir Dir einen Verifikationscode zur Rücksetzung Deines Passwortes versandt.',
        );
      } else {
        this.#msgService.addErrorMessage('Fehler bei der Anforderung eines Verifikationscodes für die Rücksetzung Deines Passwortes.');
      }
      this.displayHelp = true;
    });
  }

  public isPasswordForgotten(): boolean {
    return this.#authService.isPasswordForgotten();
  }

  public setPasswordForgottenState(): void {
    this.#authService.setPasswordForgottenState();
  }

  public setNewPasswordAfterForgotten(): void {
    this.#msgService.clearAllMessages();

    this.#authService
      .setPasswordAfterForgotten(this.#email.value, this.#verificationCode.value, this.#password.value)
      .subscribe((result) => {
        if (result.success) {
          if (result.message) {
            this.#router.navigate([commonRoutes.prelogin.home]).then(() => {
              this.#msgService.addSuccessMessage(result.message ? result.message : '');
            });
          }
        } else {
          if (result.message) {
            this.#msgService.addErrorMessage(result.message);
          }
          this.#authService.setPasswordForgottenState();
        }
      });
  }

  public resetComponentState(): void {
    this.validatedAtLeastOnce = false;
    this.#msgService.clearAllMessages();
    this.#authService.logout(false, false, undefined, {
      reason: 'ForgotPasswordComponent.resetComponentState',
    });
  }
}
